import { PRODUCTION_VISUALIZER_ENABLE_KEY, PRODUCTION_VISUALIZER_ENABLE_VALUE, REASON_FORCED, emptyObject, REASON_OFF, REASON_RULE_MATCH, REASON_TARGET_MATCH, REASON_FALLTHROUGH, REASON_NOT_FOUND, REASON_NO_EVALUATIONS } from '@shutterstock/experiments-constants';
import { setCookie, useExperimentsContext, withExperiment, useGetEvaluation } from '@shutterstock/experiments-sdk-next';
import { jsx, jsxs, Fragment } from 'react/jsx-runtime';
import { getVisualizerExperiments, getVisualizerExperimentData, getSettings, getExperimentLinks } from '@shutterstock/experiments-visualizer-shared';
import { createContext, useContext, useState, lazy, Suspense, useEffect } from 'react';

var setProductionVisEnabledCookie = function setProductionVisEnabledCookie() {
  if (typeof window !== 'undefined' && window.location) {
    var urlParams = new URLSearchParams(window.location.search);
    var setProdCookieUrlParam = urlParams.get(PRODUCTION_VISUALIZER_ENABLE_KEY);
    if (setProdCookieUrlParam === PRODUCTION_VISUALIZER_ENABLE_VALUE) {
      setCookie(PRODUCTION_VISUALIZER_ENABLE_KEY, PRODUCTION_VISUALIZER_ENABLE_VALUE);
    }
  }
};

var VisualizedExperimentsContext = createContext({});
VisualizedExperimentsContext.displayName = 'VisualizedExperimentsContext';
var useVisualizerContext = function useVisualizerContext() {
  return useContext(VisualizedExperimentsContext);
};

function _extends() {
  _extends = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends.apply(this, arguments);
}

var VisualizerProvider = function VisualizerProvider(_ref) {
  var children = _ref.children,
    visualizerSettings = _ref.visualizerSettings,
    inputEvaluations = _ref.evaluations;
  var initialEvaluations = inputEvaluations && visualizerSettings.isVisualizerOn ? getVisualizerExperiments(inputEvaluations) : {};
  var _useState = useState(initialEvaluations),
    visualizedExperiments = _useState[0],
    setVisualizedExperiments = _useState[1];
  var _useState2 = useState(false),
    isSummaryModalOpen = _useState2[0],
    setIsSummaryModalOpen = _useState2[1];
  var _useExperimentsContex = useExperimentsContext(),
    setEvaluations = _useExperimentsContex.setEvaluations,
    evaluations = _useExperimentsContex.evaluations;
  var resetExperiments = function resetExperiments() {
    setVisualizedExperiments({});
  };
  var upsertComponent = function upsertComponent(experimentId, evaluation) {
    if (visualizedExperiments[experimentId]) {
      visualizedExperiments[experimentId].reason = evaluation.reason;
      visualizedExperiments[experimentId].variationId = evaluation.variationId;
      visualizedExperiments[experimentId].renderCount += 1;
    } else {
      visualizedExperiments[experimentId] = getVisualizerExperimentData(evaluation);
      visualizedExperiments[experimentId].renderCount += 1;
    }
  };
  var setVariantId = function setVariantId(experimentId, variantId) {
    var _extends2;
    var updatedVisualizedExperiment = _extends({}, visualizedExperiments[experimentId], {
      variationId: variantId,
      reason: REASON_FORCED
    });
    setVisualizedExperiments(_extends({}, visualizedExperiments, (_extends2 = {}, _extends2[experimentId] = updatedVisualizedExperiment, _extends2)));
    if (evaluations) {
      var _extends3;
      var updatedEvaluation = _extends({}, evaluations[experimentId], {
        variationId: variantId,
        reason: REASON_FORCED
      });
      setEvaluations(_extends({}, evaluations, (_extends3 = {}, _extends3[experimentId] = updatedEvaluation, _extends3)));
    }
  };
  var visualizedExperimentsContext = {
    visualizerExperiments: visualizedExperiments,
    isSummaryModalOpen: isSummaryModalOpen,
    visualizerSettings: visualizerSettings,
    setIsSummaryModalOpen: setIsSummaryModalOpen,
    resetExperiments: resetExperiments,
    upsertComponent: upsertComponent,
    setVariantId: setVariantId
  };
  return jsx(VisualizedExperimentsContext.Provider, {
    value: visualizedExperimentsContext,
    children: children
  });
};

var VisualizerOverlay = lazy(function () {
  return import('@shutterstock/experiments-visualizer-overlay');
});
var DynamicVisualizerOverlay = function DynamicVisualizerOverlay(_ref) {
  var visualizerSettings = _ref.visualizerSettings;
  var visualizerContext = useVisualizerContext();
  return visualizerSettings.isVisualizerOn && jsx(Suspense, {
    children: jsx(VisualizerOverlay, {
      visualizerContext: visualizerContext
    })
  });
};

var VisualizedExperimentsProvider = function VisualizedExperimentsProvider(_ref) {
  var children = _ref.children,
    _ref$experiments = _ref.experiments,
    experiments = _ref$experiments === void 0 ? emptyObject : _ref$experiments;
  var evaluations = experiments.evaluations;
  setProductionVisEnabledCookie();
  var visualizerSettings = getSettings();
  if (!visualizerSettings.isVisualizerOn) {
    return children;
  }
  var visualizerEvaluations = evaluations || {};
  return jsx(VisualizerProvider, {
    evaluations: visualizerEvaluations,
    visualizerSettings: visualizerSettings,
    children: jsxs(Fragment, {
      children: [jsx(DynamicVisualizerOverlay, {
        visualizerSettings: visualizerSettings
      }), children]
    })
  });
};

var HoverDetails = function HoverDetails(_ref) {
  var experimentId = _ref.experimentId;
  var experimentLinks = getExperimentLinks(experimentId);
  return jsx("div", {
    children: jsx("div", {
      style: {
        display: 'flex',
        flexDirection: 'row'
      },
      children: experimentLinks.map(function (_ref2, ii) {
        var label = _ref2.label,
          url = _ref2.url;
        return jsxs("div", {
          children: [jsx("a", {
            target: "_blank",
            href: url,
            rel: "noreferrer",
            children: label
          }), ii !== experimentLinks.length - 1 && ",\xA0"]
        }, "HoverDetails" + ii);
      })
    })
  });
};

var _REASON_MATCH_OUTLINE;
var REASON_MATCH_OUTLINE_COLOR_MAP = (_REASON_MATCH_OUTLINE = {}, _REASON_MATCH_OUTLINE[REASON_OFF] = '#9C0000', _REASON_MATCH_OUTLINE[REASON_RULE_MATCH] = '#45CFDD', _REASON_MATCH_OUTLINE[REASON_TARGET_MATCH] = '#d4af37', _REASON_MATCH_OUTLINE[REASON_FORCED] = '#078D63', _REASON_MATCH_OUTLINE[REASON_FALLTHROUGH] = '#9681EB', _REASON_MATCH_OUTLINE[REASON_NOT_FOUND] = '#473c73', _REASON_MATCH_OUTLINE[REASON_NO_EVALUATIONS] = '##1c182e', _REASON_MATCH_OUTLINE);
var VisualizerWrapper = function VisualizerWrapper(_ref) {
  var children = _ref.children,
    experimentId = _ref.experimentId,
    reason = _ref.reason,
    variationId = _ref.variationId,
    position = _ref.position,
    isWrapperHoverOn = _ref.isWrapperHoverOn,
    isWrapperTextOn = _ref.isWrapperTextOn,
    onlyShow = _ref.onlyShow,
    isVariantReasonAlwaysShown = _ref.isVariantReasonAlwaysShown;
  var _useState = useState(false),
    isHovered = _useState[0],
    setIsFullDetailsShown = _useState[1];
  var _useVisualizerContext = useVisualizerContext(),
    upsertComponent = _useVisualizerContext.upsertComponent;
  useEffect(function () {
    upsertComponent(experimentId, {
      reason: reason,
      variationId: variationId
    });
  },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  []);
  if (onlyShow.length && !onlyShow.includes(experimentId)) return jsx(Fragment, {
    children: children
  });
  var outlineColor = reason === undefined ? 'gray' : REASON_MATCH_OUTLINE_COLOR_MAP[reason];
  var showHoverDetails = isWrapperTextOn && isWrapperHoverOn && isHovered;
  var showVariantReason = isWrapperTextOn && (isVariantReasonAlwaysShown || showHoverDetails);
  return jsxs("div", {
    onMouseEnter: function onMouseEnter() {
      return setIsFullDetailsShown(true);
    },
    onMouseLeave: function onMouseLeave() {
      return setIsFullDetailsShown(false);
    },
    style: {
      outline: "4px dashed " + outlineColor,
      margin: "3px"
    },
    children: [jsxs("div", {
      style: {
        display: 'flex',
        flexDirection: 'column',
        fontSize: 'small',
        textShadow: "#FFFFFF 0px 0px 2px",
        position: position != null ? position : 'relative'
      },
      children: [isWrapperTextOn && experimentId + ":", showVariantReason && jsx("div", {
        children: "v: " + variationId + ", r: " + ("" + reason).toLowerCase()
      }), showHoverDetails && jsx(HoverDetails, {
        experimentId: experimentId,
        reason: reason,
        variationId: variationId
      })]
    }), children]
  });
};

var withVisualizedExperiment = function withVisualizedExperiment(Feature, id, variations, doExcludeUserCheck, doVariantCustomAttributeLog) {
  var ExperimentFeature = withExperiment(Feature, id, variations, doExcludeUserCheck, doVariantCustomAttributeLog);
  var WithVisualizedExperiment = function WithVisualizedExperiment(featureProps) {
    var _visualizerContext$vi;
    var evaluation = useGetEvaluation(id);
    var visualizerContext = useVisualizerContext();
    if (!(visualizerContext != null && (_visualizerContext$vi = visualizerContext.visualizerSettings) != null && _visualizerContext$vi.isVisualizerOn)) {
      return jsx(ExperimentFeature, _extends({}, featureProps));
    }
    return jsx(VisualizerWrapper, _extends({}, evaluation, {
      experimentId: id
    }, visualizerContext.visualizerSettings, {
      children: jsx(ExperimentFeature, _extends({}, featureProps))
    }));
  };
  WithVisualizedExperiment.displayName = 'WithVisualizedExperiment';
  return WithVisualizedExperiment;
};

export { VisualizedExperimentsProvider, VisualizerProvider, useVisualizerContext, withVisualizedExperiment };
//# sourceMappingURL=index.js.map
